<template>
  <div class="additional-info-tab">
    <div class="info-cards">
      <div class="info-card">
        <div class="info-icon version">
          <span class="material-symbols-outlined">settings</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.version") }}</div>
          <div class="info-value version-display">
            <span class="version-text">{{ displayVersion || "N/A" }}</span>
            <button
              v-if="hasVersionHistory"
              @click="openModal"
              class="version-history-button"
              title="View version history"
            >
              <span class="material-symbols-outlined">history</span>
            </button>
          </div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon age">
          <span class="material-symbols-outlined">verified_user</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.ageRating") }}</div>
          <div class="info-value">{{ app.appAgeRating || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon category">
          <span class="material-symbols-outlined">category</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.category") }}</div>
          <div class="info-value">{{ app.appCategory || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon size">
          <span class="material-symbols-outlined">data_usage</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.size") }}</div>
          <div class="info-value">
            {{ app.appSize || "Varies with device" }}
          </div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon price">
          <span class="material-symbols-outlined">payments</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.price") }}</div>
          <div class="info-value">{{ app.appPrice || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon purchases">
          <span class="material-symbols-outlined">shopping_cart</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.inAppPurchases") }}</div>
          <div
            class="info-value"
            @click="handleValueClick($event)"
            v-html="formattedInAppPurchases"
          ></div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon package">
          <span class="material-symbols-outlined">package</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.packageName") }}</div>
          <div class="info-value">{{ app.appStoreId || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon copyright">
          <span class="material-symbols-outlined">copyright</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.copyright") }}</div>
          <div class="info-value">
            {{ app.appCopyright || "N/A" }}
          </div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon compatibility">
          <span class="material-symbols-outlined">devices</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.compatibility") }}</div>
          <div
            class="info-value"
            v-html="formattedCompatibility"
            @click="handleValueClick($event)"
          ></div>
        </div>
      </div>
      
      <div class="info-card">
        <div class="info-icon reviewscount">
          <span class="material-symbols-outlined">reviews</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.reviews") }}</div>
          <div class="info-value">{{ app.appReviewsCount || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon languages">
          <span class="material-symbols-outlined">translate</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.languages") }}</div>
          <div
            class="info-value"
            v-html="formattedLanguages"
            @click="handleValueClick($event)"
          ></div>
        </div>
      </div>

      <div
        class="info-card"
        v-if="
          app.appSupports != 'null' && app.appSupports && app.appSupports.length
        "
      >
        <div class="info-icon supports">
          <span class="material-symbols-outlined">extension</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.supports") }}</div>
          <div class="info-value" @click="handleValueClick($event)">
            {{ app.appSupports.join(", ") }}
          </div>
        </div>
      </div>
      
      <div class="info-card">
        <div class="info-icon releasedon">
          <span class="material-symbols-outlined">new_releases</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.relased") }}</div>
          <div class="info-value">{{ formatDateToLocale(app.appReleasedOn) || "N/A" }}</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-icon updatedon">
          <span class="material-symbols-outlined">update</span>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("Apps.updated") }}</div>
          <div class="info-value">{{ formatDateToLocale(app.appUpdatedOn) || "N/A" }}</div>
        </div>
      </div>
    </div>
  </div>

  <Modal
    :isOpen="isVersionHistoryOpen"
    :modalTitle="$t('Apps.version')"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="version-history">
      <div
        v-for="version in versionHistory"
        :key="version.versionDisplay"
        class="version-item"
      >
        <div class="version-header">
          <h3>{{ $t("Apps.version") }}: {{ version.versionDisplay }}</h3>
          <span class="version-date">{{
            formatDate(version.releaseDate)
          }}</span>
        </div>
        <span class="version-notes" v-html="version.releaseNotes"></span>
        <div class="version-timestamp">
          {{ $t("Apps.relased") }}:
          {{ formatDateTime(version.releaseTimestamp) }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/custom/modals/ModalComponent.vue";

const MAX_TEXT_LENGTH = 25;

export default {
  name: "AdditionalInfoTab",
  components: { Modal },
  props: {
    app: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isVersionHistoryOpen: false,
      expandedTexts: new Set(),
    };
  },
  computed: {
    hasVersionHistory() {
      return (
        Array.isArray(this.app.appVersions) && this.app.appVersions.length > 0
      );
    },
    displayVersion() {
      if (this.hasVersionHistory) {
        return this.app.appVersions[0].versionDisplay;
      }
      return this.app.appVersions;
    },
    versionHistory() {
      return Array.isArray(this.app.appVersions) ? this.app.appVersions : [];
    },
    formattedInAppPurchases() {
      return this.formatLongText(
        this.app.appInAppPurchases,
        MAX_TEXT_LENGTH,
        "purchases"
      );
    },
    formattedCompatibility() {
      return this.formatLongText(
        this.app.appCompatibility,
        MAX_TEXT_LENGTH,
        "compatibility"
      );
    },
    formattedLanguages() {
      return this.formatLongText(
        this.app.appLanguages,
        MAX_TEXT_LENGTH,
        "languages"
      );
    },
  },
  methods: {
    formatDateToLocale(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatLongText(text, maxLength = MAX_TEXT_LENGTH, uniqueIdSuffix = "") {
      if (text === null || text === undefined) return "N/A";
      if (typeof text !== "string") return text;

      if (text.length <= maxLength) return text;

      const safeEncode = (str) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        return btoa(String.fromCharCode(...data));
      };

      const uniqueId = safeEncode(text).substring(0, 10) + uniqueIdSuffix;
      const isExpanded = this.expandedTexts.has(uniqueId);

      if (isExpanded) {
        return `${text} <span class="read-more-link" data-id="${uniqueId}">(${this.$t(
          "Apps.showLess"
        )})</span>`;
      }

      return `${text.substring(
        0,
        maxLength
      )}... <span class="read-more-link" data-id="${uniqueId}">(${this.$t(
        "Apps.showMore"
      )})</span>`;
    },
    openModal() {
      this.isVersionHistoryOpen = true;
    },
    closeModal() {
      this.isVersionHistoryOpen = false;
    },
    toggleText(uniqueId) {
      if (this.expandedTexts.has(uniqueId)) {
        this.expandedTexts.delete(uniqueId);
      } else {
        this.expandedTexts.add(uniqueId);
      }
      this.$forceUpdate();
    },
    handleValueClick(event) {
      const textId = event.target.dataset.id;
      this.toggleText(textId);
    },
  },
};
</script>

<style scoped>
.additional-info-tab {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4px;
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
}

.info-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon .material-symbols-outlined {
  font-size: 24px;
}

.info-icon.version {
  background: #ebf5ff;
  color: #2196f3;
}

.info-icon.updated {
  background: #e8f5e9;
  color: #4caf50;
}

.info-icon.price {
  background: #fff3e0;
  color: #ff9800;
}

.info-icon.purchases {
  background: #f3e5f5;
  color: #9c27b0;
}

.info-icon.age {
  background: #e1f5fe;
  color: #03a9f4;
}

.info-icon.size {
  background: #f0f1fe;
  color: #3f51b5;
}

.info-content {
  flex: 1;
}

.info-label {
  font-size: 14px;
  color: #5f6368;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
  color: #202124;
  word-break: break-word;
}

.version-history {
  max-height: 70vh;
  overflow-y: auto;
}

.version-item {
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.version-item:last-child {
  border-bottom: none;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.version-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1a73e8;
}

.version-date {
  font-size: 14px;
  color: #666;
}

.version-notes {
  margin: 8px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.version-timestamp {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

.version-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.version-text {
  flex: 1;
}

.version-history-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: auto;
}

.version-history-button:hover {
  background: #f0f0f0;
  color: #1a73e8;
}

.version-history-button .material-symbols-outlined {
  font-size: 18px;
}

/* Scrollbar styles for version history */
.version-history::-webkit-scrollbar {
  width: 6px;
}

.version-history::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.version-history::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.version-history::-webkit-scrollbar-thumb:hover {
  background: #666;
  cursor: pointer;
}
</style>
